import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Layout from '../components/layout/Layout'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ThemeToggle from '../components/ui/ThemeToggle'

const { FiStar, FiCheck, FiGift, FiTrendingUp, FiUsers, FiHeart } = FiIcons

const LandingPage = () => {
  const features = [
    {
      icon: FiStar,
      title: 'Gamified Chores',
      description: 'Turn everyday tasks into exciting quests with points, levels, and rewards'
    },
    {
      icon: FiGift,
      title: 'Custom Rewards',
      description: 'Set up personalized rewards that motivate your kids to complete their tasks'
    },
    {
      icon: FiTrendingUp,
      title: 'Progress Tracking',
      description: 'Watch your children grow more responsible with detailed progress analytics'
    },
    {
      icon: FiUsers,
      title: 'Family-Friendly',
      description: 'Designed for the whole family with separate parent and child interfaces'
    }
  ]

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Mom of 3',
      content: 'My kids actually ASK to do their chores now! Chore Haven has transformed our household routine.',
      avatar: '👩'
    },
    {
      name: 'Mike D.',
      role: 'Dad of 2',
      content: 'Finally, a chore system that works. The gamification keeps my kids engaged and motivated.',
      avatar: '👨'
    },
    {
      name: 'Lisa K.',
      role: 'Mom of 1',
      content: 'The reward system is brilliant. My daughter has become so much more responsible!',
      avatar: '👩‍🦰'
    }
  ]

  return (
    <Layout showNavbar={false}>
      {/* Top Navigation for Landing Page */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-neutral-light dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiStar} className="text-white text-xl" />
              </div>
              <span className="font-display font-bold text-xl bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
                Chore Haven
              </span>
            </Link>

            {/* Navigation */}
            <div className="flex items-center gap-4">
              <ThemeToggle variant="simple" size="sm" />
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  Sign In
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="primary" size="sm">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16">
        {/* Enhanced Background Animation */}
        <div className="absolute inset-0 opacity-15 dark:opacity-10">
          {[...Array(25)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-4xl"
              initial={{
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
                y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
                rotate: 0,
                scale: 0.8 + Math.random() * 0.4
              }}
              animate={{
                y: [null, -30, 0],
                rotate: [0, 360],
                scale: [null, 1.2, 0.8]
              }}
              transition={{
                duration: 8 + Math.random() * 12,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 5
              }}
            >
              {['⭐', '🎁', '🏆', '🎉', '🌟', '🎈', '🦄', '🌈', '✨', '🎊'][Math.floor(Math.random() * 10)]}
            </motion.div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="inline-flex items-center gap-3 mb-6">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-xl"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <SafeIcon icon={FiStar} className="text-white text-3xl" />
                </motion.div>
                <span className="font-display font-bold text-4xl bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
                  Chore Haven
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-neutral-text dark:text-gray-100 mb-6">
                Chores Done,{' '}
                <span className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
                  Fun Won!
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
                The happy way to teach responsibility—no nagging required. Transform household chores into magical adventures for your family!
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
            >
              <Link to="/signup">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button size="xl" className="text-xl px-8 py-4 shadow-2xl">
                    <SafeIcon icon={FiStar} className="text-2xl" />
                    Start Your Free Trial
                  </Button>
                </motion.div>
              </Link>
              <Link to="/demo">
                <Button variant="outline" size="xl" className="text-xl px-8 py-4 border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20">
                  Watch Demo
                </Button>
              </Link>
            </motion.div>

            {/* Hero Image/Animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative max-w-4xl mx-auto"
            >
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-neutral-light dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Parent View Preview */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-neutral-text dark:text-gray-200">Parent Dashboard</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg">
                        <div className="text-2xl">🧹</div>
                        <div className="flex-1">
                          <div className="font-medium text-sm dark:text-gray-200">Clean Room</div>
                          <div className="text-xs text-neutral-500 dark:text-gray-400">20 points</div>
                        </div>
                        <SafeIcon icon={FiCheck} className="text-success-500" />
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-highlight-50 to-accent-50 dark:from-highlight-900/20 dark:to-accent-900/20 rounded-lg">
                        <div className="text-2xl">🍽️</div>
                        <div className="flex-1">
                          <div className="font-medium text-sm dark:text-gray-200">Load Dishwasher</div>
                          <div className="text-xs text-neutral-500 dark:text-gray-400">15 points</div>
                        </div>
                        <SafeIcon icon={FiCheck} className="text-success-500" />
                      </div>
                    </div>
                  </div>

                  {/* Child View Preview */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-neutral-text dark:text-gray-200">Kid's Adventure</h3>
                    <div className="text-center p-4 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg">
                      <div className="text-3xl mb-2">👦</div>
                      <div className="font-bold text-lg dark:text-gray-200">Level 3: Star Helper</div>
                      <div className="text-sm text-neutral-600 dark:text-gray-400 mb-3">85/100 points to next level</div>
                      <div className="w-full bg-neutral-light dark:bg-gray-700 rounded-full h-3">
                        <div className="bg-gradient-to-r from-primary-400 to-secondary-400 h-3 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-text dark:text-gray-100 mb-4">
              Why Families Love Chore Haven
            </h2>
            <p className="text-xl text-neutral-600 dark:text-gray-300 max-w-3xl mx-auto">
              We've reimagined chore management to make it fun, engaging, and effective for the whole family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="text-center h-full hover:shadow-2xl">
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <SafeIcon icon={feature.icon} className="text-white text-2xl" />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-neutral-text dark:text-gray-200 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-800 dark:to-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-text dark:text-gray-100 mb-4">
              What Parents Are Saying
            </h2>
            <p className="text-xl text-neutral-600 dark:text-gray-300">
              Real families sharing their Chore Haven success stories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <Card className="text-center h-full">
                  <div className="text-4xl mb-4">{testimonial.avatar}</div>
                  <p className="text-neutral-600 dark:text-gray-300 mb-4 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <div className="font-semibold text-neutral-text dark:text-gray-200">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-neutral-500 dark:text-gray-400">
                      {testimonial.role}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-secondary-500 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-6xl text-white"
              initial={{
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
                y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 600),
              }}
              animate={{
                rotate: [0, 360],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              ✨
            </motion.div>
          ))}
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Chore Time?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of families who've made chores the highlight of their kids' day!
            </p>
            <Link to="/signup">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button className="bg-white text-primary-600 hover:bg-gray-50 hover:text-primary-700 text-xl px-8 py-4 rounded-lg font-medium shadow-2xl transition-all duration-200 inline-flex items-center gap-3">
                  <SafeIcon icon={FiHeart} className="text-2xl text-accent-500" />
                  Start Your Free Trial Today
                </button>
              </motion.div>
            </Link>
            <p className="text-white/80 text-sm mt-4">
              No credit card required • Set up in 60 seconds
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  )
}

export default LandingPage